import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface MondrianGridProps {
  className?: string;
  animated?: boolean;
  density?: 'low' | 'medium' | 'high';
  seed?: number; // To force a specific layout if needed
}
type Block = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
};
const COLORS = ['bg-mondrian-red', 'bg-mondrian-yellow', 'bg-mondrian-blue', 'bg-mondrian-white', 'bg-mondrian-white', 'bg-mondrian-white', 'bg-mondrian-black', 'bg-mondrian-gray'];
// Simple pseudo-random number generator
const seededRandom = (seed: number) => {
  let x = Math.sin(seed++) * 10000;
  return x - Math.floor(x);
};
export function MondrianGrid({
  className = '',
  animated = false,
  density = 'medium',
  seed = Math.random()
}: MondrianGridProps) {
  const blocks = useMemo(() => {
    const generatedBlocks: Block[] = [];
    const complexity = density === 'low' ? 3 : density === 'medium' ? 5 : 8;
    let currentSeed = seed;
    // Recursive function to split rectangles
    const split = (x: number, y: number, w: number, h: number, depth: number) => {
      const rng = seededRandom(currentSeed++);
      if (depth <= 0 || w < 20 && h < 20) {
        // Assign color based on size and randomness
        let colorClass = 'bg-mondrian-white';
        const colorRng = seededRandom(currentSeed++);
        if (colorRng > 0.7) {
          // Pick a primary color
          const colorIndex = Math.floor(seededRandom(currentSeed++) * COLORS.length);
          colorClass = COLORS[colorIndex];
        }
        generatedBlocks.push({
          id: `block-${x}-${y}-${depth}`,
          x,
          y,
          w,
          h,
          color: colorClass
        });
        return;
      }
      // Decide whether to split horizontally or vertically
      // Prefer splitting the longer dimension
      const splitHorizontally = w > h ? rng > 0.3 : rng > 0.7;
      if (splitHorizontally) {
        const splitPos = 20 + Math.floor(rng * (w - 40)); // Keep min size
        split(x, y, splitPos, h, depth - 1);
        split(x + splitPos, y, w - splitPos, h, depth - 1);
      } else {
        const splitPos = 20 + Math.floor(rng * (h - 40));
        split(x, y, w, splitPos, depth - 1);
        split(x, y + splitPos, w, h - splitPos, depth - 1);
      }
    };
    // Initial canvas 100x100 units
    split(0, 0, 100, 100, complexity);
    return generatedBlocks;
  }, [density, seed]);
  return <div className={`relative w-full h-full bg-mondrian-black p-1 overflow-hidden ${className}`} data-id="element-192">
      {blocks.map(block => <motion.div key={block.id} initial={animated ? {
      opacity: 0,
      scale: 0.9
    } : {
      opacity: 1
    }} animate={animated ? {
      opacity: 1,
      scale: 1
    } : {}} transition={{
      duration: 0.5,
      delay: seededRandom(block.x + block.y) * 0.5
    }} className={`absolute border-4 border-mondrian-black ${block.color}`} style={{
      left: `${block.x}%`,
      top: `${block.y}%`,
      width: `${block.w}%`,
      height: `${block.h}%`,
      // Adjust borders to overlap correctly to form single thick lines
      boxSizing: 'border-box'
    }} data-id="element-193" />)}
    </div>;
}